import { useState } from "react";
import type { ProductionReasonDto } from "@grizzly/shared";
import { Button } from "../ui/button.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useI18n } from "../../i18n/index.js";

export interface ReportLine {
  quantity: string;
  reasonId: string;
}

export function OperationReportForm({
  reasons,
  onCancel,
  onSubmit,
}: {
  reasons: ProductionReasonDto[];
  onCancel(): void;
  onSubmit(good: string, scrap: ReportLine[], rework: ReportLine[], note: string): void;
}) {
  const { t } = useI18n();
  const [good, setGood] = useState("");
  const [scrap, setScrap] = useState<ReportLine[]>([]);
  const [rework, setRework] = useState<ReportLine[]>([]);
  const [note, setNote] = useState("");

  const scrapReasons = reasons.filter((r) => r.kind === "scrap");
  const reworkReasons = reasons.filter((r) => r.kind === "rework");

  return (
    <div className="space-y-3 rounded border border-line p-3">
      <TextInput
        label={t("shopFloor.goodQuantity")}
        required
        value={good}
        onChange={(e) => setGood(e.target.value)}
      />

      <div className="space-y-2">
        {scrap.map((line, idx) => (
          <div key={idx} className="grid items-end gap-2 sm:grid-cols-2">
            <TextInput
              label={t("shopFloor.scrapQty")}
              value={line.quantity}
              onChange={(e) =>
                setScrap((s) =>
                  s.map((l, i) => (i === idx ? { ...l, quantity: e.target.value } : l))
                )
              }
            />
            <SelectInput
              label={t("shopFloor.scrapReason")}
              options={scrapReasons.map((r) => ({ value: r.id, label: r.label }))}
              value={line.reasonId}
              onChange={(e) =>
                setScrap((s) =>
                  s.map((l, i) => (i === idx ? { ...l, reasonId: e.target.value } : l))
                )
              }
            />
          </div>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            setScrap((s) => [...s, { quantity: "", reasonId: scrapReasons[0]?.id ?? "" }])
          }
        >
          {t("shopFloor.addScrap")}
        </Button>
      </div>

      <div className="space-y-2">
        {rework.map((line, idx) => (
          <div key={idx} className="grid items-end gap-2 sm:grid-cols-2">
            <TextInput
              label={t("shopFloor.reworkQty")}
              value={line.quantity}
              onChange={(e) =>
                setRework((s) =>
                  s.map((l, i) => (i === idx ? { ...l, quantity: e.target.value } : l))
                )
              }
            />
            <SelectInput
              label={t("shopFloor.reworkReason")}
              options={reworkReasons.map((r) => ({ value: r.id, label: r.label }))}
              value={line.reasonId}
              onChange={(e) =>
                setRework((s) =>
                  s.map((l, i) => (i === idx ? { ...l, reasonId: e.target.value } : l))
                )
              }
            />
          </div>
        ))}
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            setRework((s) => [
              ...s,
              { quantity: "", reasonId: reworkReasons[0]?.id ?? "" },
            ])
          }
        >
          {t("shopFloor.addRework")}
        </Button>
      </div>

      <TextInput
        label={t("shopFloor.note")}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={!good.trim() || Number(good) <= 0}
          onClick={() =>
            onSubmit(
              good,
              scrap.filter((s) => s.quantity && Number(s.quantity) > 0 && s.reasonId),
              rework.filter((r) => r.quantity && Number(r.quantity) > 0 && r.reasonId),
              note
            )
          }
        >
          {t("shopFloor.completeOperation")}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </div>
    </div>
  );
}
